import { useEffect, useMemo, useState } from 'react'
import { type ReactNode } from 'react'
import { Check, Tags, Users } from 'lucide-react'
import {
  type AudioGroup,
  type AudioTrackBindingKind
} from '../../../api'
import type { Label } from '../../../api/labels'
import { Modal, SearchField } from '../../../components/ui'
import { labelCategoryLabels, labelCategoryOptions } from '../../labels/labelDisplay'
import styles from './AudioAssignmentsTargetPickerDialog.module.css'

type AudioAssignmentsTargetPickerDialogProps = {
  activeLabels: Label[]
  groups: AudioGroup[]
  kind: AudioTrackBindingKind
  onClose: () => void
  onSelect: (kind: AudioTrackBindingKind, targetId: string) => void
  targetId: string
}

const targetTabs: Array<{ icon: ReactNode; label: string; value: AudioTrackBindingKind }> = [
  { icon: <Tags size={15} />, label: 'Tags', value: 'label' },
  { icon: <Users size={15} />, label: 'Groups', value: 'group' }
]

const matchesQuery = (value: string, query: string): boolean => {
  return value.toLowerCase().includes(query)
}

export const AudioAssignmentsTargetPickerDialog = ({
  activeLabels,
  groups,
  kind,
  onClose,
  onSelect,
  targetId
}: AudioAssignmentsTargetPickerDialogProps) => {
  const [activeTab, setActiveTab] = useState<AudioTrackBindingKind>(kind)
  const [searchInput, setSearchInput] = useState('')
  const query = searchInput.trim().toLowerCase()

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  const filteredLabelCategories = useMemo(() => {
    return labelCategoryOptions
      .map((category) => ({
        category,
        labels: activeLabels.filter((label) => {
          return label.category === category
            && (!query || matchesQuery(label.name, query) || matchesQuery(label.description ?? '', query))
        })
      }))
      .filter((category) => category.labels.length > 0)
  }, [activeLabels, query])

  const filteredGroups = useMemo(() => {
    if (!query) {
      return groups
    }

    return groups.filter((group) => {
      return matchesQuery(group.name, query) || matchesQuery(group.description ?? '', query)
    })
  }, [groups, query])

  const selectTarget = (nextKind: AudioTrackBindingKind, nextTargetId: string): void => {
    onSelect(nextKind, nextTargetId)
    onClose()
  }

  return (
    <Modal
      backdropClassName={styles.backdrop ?? ''}
      mobilePresentation="sheet"
      onClose={onClose}
      panelClassName={styles.panel ?? ''}
      title={<h2>Select assignment target</h2>}
    >
      <div className={styles.tabs} role="tablist" aria-label="Assignment target type">
        {targetTabs.map((tab) => (
          <button
            aria-selected={activeTab === tab.value}
            className={activeTab === tab.value ? styles.tabActive : styles.tab}
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            role="tab"
            type="button"
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      <SearchField
        className={styles.search}
        onChange={(event) => setSearchInput(event.target.value)}
        placeholder={activeTab === 'label' ? 'Search tags...' : 'Search groups...'}
        size="mobile"
        value={searchInput}
      />

      <div className={styles.list}>
        {activeTab === 'label' && (
          filteredLabelCategories.length > 0 ? filteredLabelCategories.map(({ category, labels }) => (
            <section className={styles.category} key={category}>
              <h3>{labelCategoryLabels[category]}</h3>
              <div className={styles.labelItems}>
                {labels.map((label) => {
                  const selected = kind === 'label' && targetId === label.id

                  return (
                    <button
                      aria-pressed={selected}
                      className={selected ? styles.labelItemSelected : styles.labelItem}
                      key={label.id}
                      onClick={() => selectTarget('label', label.id)}
                      type="button"
                    >
                      <span className={styles.labelName}>{label.name}</span>
                      <small>{label.track_count}</small>
                      {selected && <Check size={16} />}
                    </button>
                  )
                })}
              </div>
            </section>
          )) : (
            <p className={styles.empty}>No tags match this search.</p>
          )
        )}

        {activeTab === 'group' && (
          filteredGroups.length > 0 ? (
            <div className={styles.items}>
              {filteredGroups.map((group) => {
                const selected = kind === 'group' && targetId === group.id

                return (
                  <button
                    aria-pressed={selected}
                    className={selected ? styles.itemSelected : styles.item}
                    key={group.id}
                    onClick={() => selectTarget('group', group.id)}
                    type="button"
                  >
                    <span className={styles.labelName}>{group.name}</span>
                    <small>{group.track_count} tracks</small>
                    {selected && <Check size={16} />}
                  </button>
                )
              })}
            </div>
          ) : (
            <p className={styles.empty}>No groups match this search.</p>
          )
        )}
      </div>
    </Modal>
  )
}
